import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../css/AddUnit.css";

const AddPurchase = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [productId, setProductId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [unitName, setUnitName] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [quantity, setQuantity] = useState("");
  const [perUnitPrice, setPerUnitPrice] = useState("");
  const [paidAmount, setPaidAmount] = useState("0.00");
  const [dueAmount, setDueAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Product search dropdown state
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const productDropdownRef = useRef(null);

  // Supplier search dropdown state
  const [supplierSearch, setSupplierSearch] = useState("");
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1);
  const supplierDropdownRef = useRef(null);

  // Add Product modal state and logic
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [addProductName, setAddProductName] = useState("");
  const [addUnitId, setAddUnitId] = useState("");
  const [addUnits, setAddUnits] = useState([]);
  const [isAddProductSubmitting, setIsAddProductSubmitting] = useState(false);
  const [isLoadingAddUnits, setIsLoadingAddUnits] = useState(false);
  const [isLoadingAddProduct, setIsLoadingAddProduct] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Get selected product name for display
  const getSelectedProductName = () => {
    const selectedProduct = products.find(p => p.id === parseInt(productId));
    return selectedProduct ? selectedProduct.product : "";
  };

  // Get selected supplier name for display
  const getSelectedSupplierName = () => {
    const selectedSupplier = suppliers.find(s => s.id === parseInt(supplierId));
    return selectedSupplier ? selectedSupplier.suppliername : "";
  };

  // Filter products based on search
  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.product.toLowerCase().includes(productSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
    setSelectedProductIndex(-1);
  }, [productSearch, products]);

  // Filter suppliers based on search
  useEffect(() => {
    if (!supplierSearch.trim()) {
      setFilteredSuppliers(suppliers);
    } else {
      const filtered = suppliers.filter(supplier =>
        supplier.suppliername.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
    setSelectedSupplierIndex(-1);
  }, [supplierSearch, suppliers]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
        setSelectedProductIndex(-1);
      }
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierDropdown(false);
        setSelectedSupplierIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle product selection
  const handleProductSelect = (product) => {
    setProductId(product.id.toString());
    setProductSearch(product.product);
    setShowProductDropdown(false);
    setSelectedProductIndex(-1);
  };

  // Handle product keyboard navigation
  const handleProductKeyDown = (e) => {
    if (!showProductDropdown || filteredProducts.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedProductIndex(prev => {
          const newIndex = prev < filteredProducts.length - 1 ? prev + 1 : 0;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown = productDropdownRef.current?.querySelector('.product-dropdown');
            const selectedItem = dropdown?.querySelector(`li:nth-child(${newIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedProductIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredProducts.length - 1;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown = productDropdownRef.current?.querySelector('.product-dropdown');
            const selectedItem = dropdown?.querySelector(`li:nth-child(${newIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedProductIndex >= 0 && selectedProductIndex < filteredProducts.length) {
          handleProductSelect(filteredProducts[selectedProductIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        setSelectedProductIndex(-1);
        break;
    }
  };

  // Handle product search input
  const handleProductSearchChange = (e) => {
    setProductSearch(e.target.value);
    setShowProductDropdown(true);
    setSelectedProductIndex(-1);
    if (!e.target.value.trim()) {
      setProductId("");
    }
  };

  // Handle product search focus
  const handleProductSearchFocus = () => {
    setShowProductDropdown(true);
    setSelectedProductIndex(-1);
  };

  // Handle supplier selection
  const handleSupplierSelect = (supplier) => {
    setSupplierId(supplier.id.toString());
    setSupplierSearch(supplier.suppliername);
    setShowSupplierDropdown(false);
    setSelectedSupplierIndex(-1);
  };

  // Handle supplier keyboard navigation
  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSupplierIndex(prev => {
          const newIndex = prev < filteredSuppliers.length - 1 ? prev + 1 : 0;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown = supplierDropdownRef.current?.querySelector('.product-dropdown');
            const selectedItem = dropdown?.querySelector(`li:nth-child(${newIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSupplierIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : filteredSuppliers.length - 1;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown = supplierDropdownRef.current?.querySelector('.product-dropdown');
            const selectedItem = dropdown?.querySelector(`li:nth-child(${newIndex + 1})`);
            if (selectedItem) {
              selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSupplierIndex >= 0 && selectedSupplierIndex < filteredSuppliers.length) {
          handleSupplierSelect(filteredSuppliers[selectedSupplierIndex]);
        }
        break;
      case 'Escape':
        setShowSupplierDropdown(false);
        setSelectedSupplierIndex(-1);
        break;
    }
  };

  // Handle supplier search input
  const handleSupplierSearchChange = (e) => {
    setSupplierSearch(e.target.value);
    setShowSupplierDropdown(true);
    setSelectedSupplierIndex(-1);
    if (!e.target.value.trim()) {
      setSupplierId("");
    }
  };

  // Handle supplier search focus
  const handleSupplierSearchFocus = () => {
    setShowSupplierDropdown(true);
    setSelectedSupplierIndex(-1);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [supplierRes, productRes] = await Promise.all([
          fetch(`${API_URL}supplier-list.php`),
          fetch(`${API_URL}product-list.php`),
        ]);
        const supplierData = await supplierRes.json();
        const productData = await productRes.json();
        if (Array.isArray(supplierData.suppliers))
          setSuppliers(supplierData.suppliers);
        if (productData.status && Array.isArray(productData.data))
          setProducts(productData.data);
      } catch (error) {
        showToast(`Failed to fetch initial data`, "error");
      }
    };
    fetchInitialData();
  }, [API_URL]);

  useEffect(() => {
    setUnitId("");
    setUnitName("");
    if (!productId) return;
    const fetchUnit = async () => {
      try {
        const response = await fetch(
          `${API_URL}get-unit-by-product.php?product_id=${productId}`
        );
        const data = await response.json();
        if (data.status === true) {
          setUnitId(data.unit_id);
          setUnitName(data.unitname);
        } else {
          showToast(
            data.message || "Could not find a unit for this product.",
            "error"
          );
        }
      } catch (error) {
        showToast("Error fetching product unit.", "error");
      }
    };
    fetchUnit();
  }, [productId, API_URL]);

  useEffect(() => {
    const total = parseFloat(quantity) * parseFloat(perUnitPrice);
    const paid = parseFloat(paidAmount);
    if (!isNaN(total)) {
      setTotalAmount(total);
      setDueAmount(!isNaN(paid) ? total - paid : total);
    } else {
      setTotalAmount(0);
      setDueAmount(0);
    }
  }, [quantity, perUnitPrice, paidAmount]);

  // Fetch units for Add Product modal
  useEffect(() => {
    if (!showAddProductModal) return;
    const fetchUnits = async () => {
      setIsLoadingAddUnits(true);
      try {
        const response = await fetch(`${API_URL}add-unit-list.php`);
        const data = await response.json();
        if (data.units) {
          setAddUnits(data.units);
        } else {
          setAddUnits([]);
        }
      } catch (error) {
        setAddUnits([]);
      } finally {
        setIsLoadingAddUnits(false);
      }
    };
    fetchUnits();
  }, [showAddProductModal, API_URL]);

  // Add Product form submit handler
  const handleAddProductSubmit = async (e) => {
    e.preventDefault();
    if (!addProductName.trim()) {
      showToast('Please enter a product name', 'error');
      return;
    }
    if (!addUnitId) {
      showToast('Please select a unit', 'error');
      return;
    }
    setIsAddProductSubmitting(true);
    setIsLoadingAddProduct(true);
    try {
      const formData = new FormData();
      formData.append('product', addProductName.trim());
      formData.append('unitname_id', addUnitId);
      const response = await fetch(`${API_URL}add-product.php`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          showToast(`Product "${addProductName}" added successfully!`, 'success');
          setShowAddProductModal(false);
          setAddProductName('');
          setAddUnitId('');
          // Refresh product list for dropdown
          const prodRes = await fetch(`${API_URL}product-list.php`);
          const prodData = await prodRes.json();
          if (prodData.status && Array.isArray(prodData.data)) {
            setProducts(prodData.data);
            // Auto-select the newly added product
            const newProduct = prodData.data.find(
              p => p.product && p.product.trim().toLowerCase() === addProductName.trim().toLowerCase()
            );
            if (newProduct) {
              setProductId(newProduct.id.toString());
              setProductSearch(newProduct.product);
            }
          }
        } else {
          showToast(result.message || 'Error adding product. Please try again.', 'error');
        }
      } else {
        showToast('Network error. Please try again.', 'error');
      }
    } catch (error) {
      showToast('Error saving product. Please try again.', 'error');
    } finally {
      setIsAddProductSubmitting(false);
      setIsLoadingAddProduct(false);
    }
  };

  // Function to reset the form fields
  const resetForm = () => {
    setSupplierId("");
    setProductId("");
    setUnitId("");
    setUnitName("");
    setInvoiceNo("");
    setPurchaseDate(new Date().toISOString().split("T")[0]);
    setQuantity("");
    setPerUnitPrice("");
    setPaidAmount("0.00");
    setDueAmount(0);
    setTotalAmount(0);
    setPaymentType("");
    setSupplierSearch("");
    setProductSearch("");
    setSelectedSupplierIndex(-1);
    setSelectedProductIndex(-1);
    setShowSupplierDropdown(false);
    setShowProductDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const purchaseData = {
      supplier_id: Number(supplierId),
      product_id: Number(productId),
      invoice_no: invoiceNo,
      quantity: Number(quantity),
      per_unit_price: Number(perUnitPrice),
      unit_id: Number(unitId),
      paid_amount: Number(paidAmount),
      due_amount: Number(dueAmount),
      purchase_date: purchaseDate,
      payment_type: paidAmount && parseFloat(paidAmount) > 0 ? paymentType.toLowerCase() : "",
    };

    try {
      const response = await fetch(`${API_URL}add-purchase.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(purchaseData),
      });
      const data = await response.json();
      if (data.status === true) {
        showToast(data.message, "success");
        resetForm();
      } else {
        showToast(data.message || "Failed to add purchase.", "error");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1>Add Purchase</h1>
          <p>Record a new purchase from a supplier</p>
        </div>
        <button
          className="btn btn-primary"
          style={{ height: "40px", fontWeight: "bold", fontSize: "1em", padding: "0 20px", marginLeft: "auto" }}
          onClick={() => setShowAddProductModal(true)}
        >
          + Add Product
        </button>
      </div>
      <div className="form-container" style={{ maxWidth: "90%" }}>
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px 30px",
            }}
          >
            <div className="form-group">
              <label htmlFor="supplierId">Supplier</label>
              <div ref={supplierDropdownRef} className={`product-search-container ${showSupplierDropdown ? 'open' : ''}`}>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    id="supplierId"
                    value={supplierSearch}
                    onChange={handleSupplierSearchChange}
                    onFocus={handleSupplierSearchFocus}
                    onKeyDown={handleSupplierKeyDown}
                    placeholder="Search suppliers..."
                    disabled={isSubmitting}
                    className="search-input"
                  />
                  <button
                    type="button"
                    className="dropdown-toggle"
                    onClick={() => {
                      setShowSupplierDropdown(!showSupplierDropdown);
                      if (!showSupplierDropdown) {
                        setTimeout(() => {
                          const input = supplierDropdownRef.current?.querySelector('input');
                          input?.focus();
                          // Scroll to selected item if exists
                          if (selectedSupplierIndex >= 0) {
                            const selectedElement = supplierDropdownRef.current?.querySelector('.product-dropdown li.selected');
                            if (selectedElement) {
                              selectedElement.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center' 
                              });
                            }
                          }
                        }, 100);
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                </div>
                {showSupplierDropdown && (
                  <ul className="product-dropdown">
                    {filteredSuppliers.length > 0 ? (
                      filteredSuppliers.map((supplier, index) => (
                        <li
                          key={supplier.id}
                          data-supplier-id={supplier.id}
                          onClick={() => handleSupplierSelect(supplier)}
                          className={selectedSupplierIndex === index ? "selected" : ""}
                          onMouseEnter={(e) => {
                            // Remove selection from other items
                            supplierDropdownRef.current?.querySelectorAll('.product-dropdown li').forEach(item => {
                              item.classList.remove('selected');
                            });
                            // Add selection to current item
                            e.target.classList.add('selected');
                            setSelectedSupplierIndex(index);
                          }}
                        >
                          {supplier.suppliername}
                        </li>
                      ))
                    ) : (
                      <li className="no-results">No suppliers found</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="invoiceNo">Invoice No</label>
              <input
                type="text"
                id="invoiceNo"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="purchaseDate">Purchase Date</label>
              <input
                type="date"
                id="purchaseDate"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="productId">Product</label>
              <div ref={productDropdownRef} className={`product-search-container ${showProductDropdown ? 'open' : ''}`}>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    id="productId"
                    value={productSearch}
                    onChange={handleProductSearchChange}
                    onFocus={handleProductSearchFocus}
                    onKeyDown={handleProductKeyDown}
                    placeholder="Search products..."
                    disabled={isSubmitting}
                    className="search-input"
                  />
                  <button
                    type="button"
                    className="dropdown-toggle"
                    onClick={() => {
                      setShowProductDropdown(!showProductDropdown);
                      if (!showProductDropdown) {
                        setTimeout(() => {
                          const input = productDropdownRef.current?.querySelector('input');
                          input?.focus();
                          // Scroll to selected item if exists
                          if (selectedProductIndex >= 0) {
                            const selectedElement = productDropdownRef.current?.querySelector('.product-dropdown li.selected');
                            if (selectedElement) {
                              selectedElement.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center' 
                              });
                            }
                          }
                        }, 100);
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                </div>
                {showProductDropdown && (
                  <ul className="product-dropdown">
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product, index) => (
                        <li
                          key={product.id}
                          data-product-id={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={selectedProductIndex === index ? "selected" : ""}
                          onMouseEnter={(e) => {
                            // Remove selection from other items
                            productDropdownRef.current?.querySelectorAll('.product-dropdown li').forEach(item => {
                              item.classList.remove('selected');
                            });
                            // Add selection to current item
                            e.target.classList.add('selected');
                            setSelectedProductIndex(index);
                          }}
                        >
                          {product.product}
                        </li>
                      ))
                    ) : (
                      <li className="no-results">No products found</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="quantity">Quantity</label>
              <input
                type="number"
                id="quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                disabled={isSubmitting}
                onWheel={e => e.target.blur()}
              />
            </div>
          
            <div className="form-group">
              <label htmlFor="unitName">Unit</label>
              <input
                type="text"
                id="unitName"
                value={unitName}
                readOnly
                disabled
                placeholder="Select product to see unit"
              />
            </div>
           
            <div className="form-group">
              <label htmlFor="perUnitPrice">Price Per Unit</label>
              <input
                type="number"
                id="perUnitPrice"
                value={perUnitPrice}
                onChange={(e) => setPerUnitPrice(e.target.value)}
                required
                disabled={isSubmitting}
                onWheel={e => e.target.blur()}
              />
            </div>
            <div className="form-group">
              <label htmlFor="paidAmount">Paid Amount</label>
              <input
                type="number"
                id="paidAmount"
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                required
                disabled={true}
                onWheel={e => e.target.blur()}
              />
            </div>
            {paidAmount && parseFloat(paidAmount) > 0 && (
              <div className="form-group">
                <label htmlFor="paymentType">Payment Type</label>
                <select
                  id="paymentType"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Payment Type</option>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Total Amount</label>
              <input
                type="number"
                value={totalAmount.toFixed(2)}
                readOnly
                disabled
                onWheel={e => e.target.blur()}
              />
            </div>
            <div className="form-group">
              <label>Due Amount</label>
              <input
                type="number"
                value={dueAmount.toFixed(2)}
                readOnly
                disabled
                style={{
                  fontWeight: "bold",
                  color: dueAmount > 0 ? "red" : "green",
                }}
                onWheel={e => e.target.blur()}
              />
            </div>
          </div>
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
            style={{ marginTop: "20px" }}
          >
            {isSubmitting ? "Saving..." : "Save Purchase"}
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              {" "}
              ×{" "}
            </button>
          </div>
        </div>
      )}
      {showAddProductModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 400, position: "relative" }}>
            {/* Close icon */}
            <button
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "none",
                border: "none",
                fontSize: "1.5em",
                cursor: "pointer",
                color: "#333"
              }}
              aria-label="Close"
              onClick={() => setShowAddProductModal(false)}
              disabled={isAddProductSubmitting || isLoadingAddUnits || isLoadingAddProduct}
            >
              ×
            </button>
            {/* Modal title left aligned */}
            <h3 style={{ marginBottom: 20, textAlign: "left" }}>Add Product</h3>
            <form onSubmit={handleAddProductSubmit} className="add-unit-form">
              <div className="form-group">
                <label htmlFor="addProductName" style={{ textAlign: 'left', display: 'block' }}>Product Name</label>
                <input
                  type="text"
                  id="addProductName"
                  name="addProductName"
                  value={addProductName}
                  onChange={(e) => setAddProductName(e.target.value)}
                  placeholder="Enter product name (e.g., Rice, Sugar, Oil)"
                  required
                  disabled={isAddProductSubmitting || isLoadingAddProduct}
                />
                {isLoadingAddProduct && <div className="loading-text">Loading product details...</div>}
              </div>
              <div className="form-group">
                <label htmlFor="addUnitId" style={{ textAlign: 'left', display: 'block' }}>Unit</label>
                <select
                  id="addUnitId"
                  name="addUnitId"
                  value={addUnitId}
                  onChange={(e) => setAddUnitId(e.target.value)}
                  required
                  disabled={isAddProductSubmitting || isLoadingAddUnits || isLoadingAddProduct}
                >
                  <option value="">Select a unit</option>
                  {addUnits.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.unitname}
                    </option>
                  ))}
                </select>
                {isLoadingAddUnits && <div className="loading-text">Loading units...</div>}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddProductModal(false)}
                  disabled={isAddProductSubmitting || isLoadingAddUnits || isLoadingAddProduct}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isAddProductSubmitting || isLoadingAddUnits || isLoadingAddProduct}
                >
                  {isAddProductSubmitting ? "Adding Product..." : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPurchase;
